import React, { useState, useRef, useEffect } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { Button } from 'src/components/ui/button';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';

interface MultiSelectorProps {
  selected: {id: string | number, name: string}[];
  items: Array<{id: string | number, name: string}>;
  onSelectionChange: (items: {id: string | number, name: string}[]) => void;
  placeholder: string;
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const MultiSelector: React.FC<MultiSelectorProps> = ({
  selected,
  items,
  onSelectionChange,
  label,
  placeholder,
  isLoading = false,
  disabled = false,
}) => {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const [selectedItems, setSelectedItems] = useState<{id: string | number, name: string}[]>(selected);

  // Sync internal state with prop changes
  useEffect(() => {
    setSelectedItems(selected);
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectionChange = (item: {id: string | number, name: string}) => {
    if (selectedItems.some(selectedItem => selectedItem.id === item.id)) {
      setSelectedItems(selectedItems.filter(selectedItem => selectedItem.id !== item.id));
      onSelectionChange(selectedItems.filter(selectedItem => selectedItem.id !== item.id));
      return;
    } else {
      setSelectedItems([...selectedItems, item]);
      onSelectionChange([...selectedItems, item]);
    }
  };

  const handleSelectAll = () => {
    setSelectedItems(items);
    onSelectionChange(items);
  };

  const handleSelectNone = () => {
    setSelectedItems([]);
    onSelectionChange([]);
  };


  const displayPlaceholder = selectedItems.length === 0 ? placeholder :
    selectedItems.length === items.length ? "All Selected" :
      `${selectedItems.length} selected`;

  return (
    <div className="relative w-full overflow-visible">
      {label && (
        <label className="absolute -top-5 left-3 text-xs font-medium text-gray-500 px-1 pointer-events-none">
          {label}
        </label>
      )}
      <div className="relative">
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={isLoading}
            asChild
          >
            <Button
              className="border-none bg-gray-201"
              variant="ghost"
              size="med"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />}
              {isLoading ? 'Loading...' : displayPlaceholder}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="p-0 m-0"
            align='start'
            sideOffset={5}
            side='bottom'
          >
            <div className={`text-sm text-gray-700 font-medium flex justify-between w-full min-w-[200px]`}>
              <Input
                className='w-full shadow-none rounded-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent'
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className={`text-sm text-gray-700 font-medium flex justify-between w-full min-w-[200px]`}>
              <div
                onClick={() => handleSelectAll()}
                className={`hover:bg-gray-200 hover:text-gray-700 p-2 px-4 cursor-pointer text-center w-full border-r border-b border-gray-201`}
              >
                Select All
              </div>
              <div
                onClick={() => handleSelectNone()}
                className={`hover:bg-gray-200 hover:text-gray-700 p-2 px-4 cursor-pointer text-center w-full border-b border-gray-201`}
              >
                Select None
              </div>

            </div>

            <div className="flex flex-col w-full">
              {filteredItems.map(item => (
                <div
                  onClick={() => handleSelectionChange(item)}
                  key={item.id}
                  className={`text-sm text-gray-700 font-medium p-2 px-4 w-full min-w-[200px] cursor-pointer ${selectedItems.some(selectedItem => selectedItem.id === item.id) ? 'bg-blue-500 text-white hover:bg-blue-300 hover:text-white' : 'hover:bg-gray-200 hover:text-gray-700'}`}
                >
                  {item.name}
                  <div className={`text-xs font-normal ${selectedItems.some(selectedItem => selectedItem.id === item.id) ? '' : ''}`}>
                    {item.id}
                  </div>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>
    </div>
  );
};

export default MultiSelector;
